<template>
  <nav class="app-pagination" role="navigation" :aria-label="ariaLabel">
    <ul class="app-pagination__list">
      <li class="app-pagination__item">
        <button
          type="button"
          class="app-pagination__btn app-pagination__btn--nav"
          :disabled="disabled || value <= 1"
          aria-label="Previous page"
          @click="goTo(value - 1)"
        >‹</button>
      </li>

      <template v-for="(page, idx) in visiblePages">
        <li
          v-if="page === '…'"
          :key="`gap-${idx}`"
          class="app-pagination__item app-pagination__item--gap"
          aria-hidden="true"
        >
          <span class="app-pagination__btn app-pagination__btn--gap">…</span>
        </li>
        <li v-else :key="`p-${page}`" class="app-pagination__item">
          <button
            type="button"
            class="app-pagination__btn"
            :class="{ 'is-active': page === value }"
            :disabled="disabled"
            :aria-current="page === value ? 'page' : null"
            @click="goTo(page)"
          >{{ page }}</button>
        </li>
      </template>

      <li class="app-pagination__item">
        <button
          type="button"
          class="app-pagination__btn app-pagination__btn--nav"
          :disabled="disabled || value >= pages"
          aria-label="Next page"
          @click="goTo(value + 1)"
        >›</button>
      </li>
    </ul>

    <form
      v-if="showJump && pages > 1"
      class="app-pagination__jump"
      @submit.prevent="onJump"
    >
      <span class="app-pagination__jump-label">Go to</span>
      <input
        v-model="jumpInput"
        type="text"
        inputmode="numeric"
        class="app-pagination__jump-input"
        :aria-label="`Jump to a page (1 to ${pages})`"
        :disabled="disabled"
      />
      <span class="app-pagination__jump-total">/ {{ pages }}</span>
      <button
        type="submit"
        class="app-pagination__jump-btn"
        :disabled="disabled"
        aria-label="Go to page"
      >→</button>
    </form>
  </nav>
</template>

<script>
export default {
  name: 'AppPagination',
  model: { prop: 'value', event: 'update:value' },
  props: {
    value: { type: Number, default: 1 },
    pages: { type: Number, default: 1 },
    maxVisible: { type: Number, default: 7 },
    showJump: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    ariaLabel: { type: String, default: 'Pagination' }
  },
  data () {
    return {
      jumpInput: String(this.value || 1)
    }
  },
  computed: {
    visiblePages () {
      const total = Math.max(1, Number(this.pages || 1))
      const cur = Math.min(Math.max(1, Number(this.value || 1)), total)
      const max = Math.max(5, Number(this.maxVisible || 7))

      if (total <= max) {
        return Array.from({ length: total }, (_, i) => i + 1)
      }

      const sideCount = Math.floor((max - 3) / 2)
      let left = Math.max(2, cur - sideCount)
      let right = Math.min(total - 1, cur + sideCount)

      if (cur - 1 <= sideCount) {
        right = Math.min(total - 1, max - 2)
      }
      if (total - cur <= sideCount) {
        left = Math.max(2, total - max + 3)
      }

      const out = [1]
      if (left > 2) out.push('…')
      for (let p = left; p <= right; p++) out.push(p)
      if (right < total - 1) out.push('…')
      out.push(total)
      return out
    }
  },
  watch: {
    value (now) {
      this.jumpInput = String(now)
    }
  },
  methods: {
    goTo (page) {
      const total = Math.max(1, Number(this.pages || 1))
      const target = Math.min(Math.max(1, Math.floor(Number(page) || 1)), total)
      if (target === Number(this.value)) return
      this.$emit('update:value', target)
      this.$emit('input', target)
      this.$emit('change', target)
    },
    onJump () {
      const n = Math.floor(Number(this.jumpInput))
      if (!Number.isFinite(n) || n < 1) {
        this.jumpInput = String(this.value)
        return
      }
      this.goTo(n)
    }
  }
}
</script>

<style scoped>
.app-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.app-pagination__list {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.app-pagination__item {
  margin: 0;
}

.app-pagination__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 0.6rem;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
  color: #4b5563;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
  font-family: inherit;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.app-pagination__btn:hover:not(:disabled):not(.is-active) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.app-pagination__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.22);
}

.app-pagination__btn.is-active {
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border-color: transparent;
  color: #ffffff;
  box-shadow: var(--brand-shadow, 0 4px 10px rgba(14, 91, 70, 0.28));
  cursor: default;
}

.app-pagination__btn.is-active:hover {
  filter: brightness(1.05);
}

.app-pagination__btn:disabled {
  background: #fbfcfe;
  border-color: #eef1f7;
  color: #9ca3af;
  cursor: not-allowed;
  pointer-events: none;
  box-shadow: none;
}

.app-pagination__btn--nav {
  font-size: 1rem;
  font-weight: 700;
}

.app-pagination__btn--gap {
  cursor: default;
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  color: #9ca3af;
}

.app-pagination__jump {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #4b5563;
}

.app-pagination__jump-label {
  font-weight: 600;
  color: #6b7280;
}

.app-pagination__jump-input {
  width: 56px;
  height: 32px;
  padding: 0 0.5rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
  background: #ffffff;
  font-variant-numeric: tabular-nums;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.app-pagination__jump-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.app-pagination__jump-input:disabled {
  background: #f8fafc;
  color: #9ca3af;
  cursor: not-allowed;
}

.app-pagination__jump-total {
  color: #6b7280;
}

.app-pagination__jump-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
  color: #1f2a44;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.app-pagination__jump-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.app-pagination__jump-btn:disabled {
  background: #f8fafc;
  color: #9ca3af;
  cursor: not-allowed;
}

@media (max-width: 575px) {
  .app-pagination {
    justify-content: center;
  }

  .app-pagination__list {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
